import { useState, useEffect } from "react";
import {
  fetchCheck as apiCheck,
  fetchApply,
  fetchSettings,
  formatAmount,
  formatPhone,
  type CheckResult,
} from "@/lib/staff-api";

const STORES = ["명동점", "동대문점"];

export default function PosPage() {
  const [phone, setPhone] = useState("");
  const [checkResult, setCheckResult] = useState<CheckResult | null>(null);
  const [discountRate, setDiscountRate] = useState(30);
  const [discountPin, setDiscountPin] = useState("");
  const [selectedStore, setSelectedStore] = useState(STORES[0]);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");

  const [pinInput, setPinInput] = useState("");
  const [pinVerified, setPinVerified] = useState(false);
  const [pinError, setPinError] = useState("");

  const [purchase, setPurchase] = useState("");
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState<{ ok: boolean; msg: string; discountAmt: number; purchaseAmt: number; remaining?: number } | null>(null);

  useEffect(() => {
    fetchSettings().then(s => {
      setDiscountRate(s.discount_rate);
      setDiscountPin(s.discount_pin ?? "");
    }).catch(() => {});
  }, []);

  function handlePhoneInput(v: string) {
    const digits = v.replace(/\D/g, "").slice(0, 11);
    setPhone(digits);
    setCheckResult(null); setApplyResult(null); setError("");
    setPinInput(""); setPinVerified(false); setPinError("");
  }

  async function handleCheck() {
    if (phone.length < 9) { setError("전화번호를 정확히 입력하세요"); return; }
    setChecking(true); setError(""); setCheckResult(null); setApplyResult(null);
    setPinInput(""); setPinVerified(false); setPinError(""); setPurchase("");
    try {
      const r = await apiCheck(phone);
      setCheckResult(r);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "조회 오류");
    } finally { setChecking(false); }
  }

  function handleVerifyPin() {
    if (!discountPin) { setPinVerified(true); return; }
    if (pinInput.trim() === discountPin.trim()) {
      setPinVerified(true); setPinError("");
    } else {
      setPinError("비밀번호가 일치하지 않습니다. 다시 확인해주세요.");
    }
  }

  async function handleApply() {
    if (!checkResult?.employee) return;
    const purchaseN = parseInt(purchase.replace(/,/g, ""), 10);
    if (isNaN(purchaseN) || purchaseN <= 0) { setError("구매금액을 입력하세요"); return; }
    const appliedRate = checkResult.discountRate ?? discountRate;
    const discountAmt = Math.floor(purchaseN * appliedRate / 100);
    if (discountAmt <= 0) { setError("할인금액이 0원입니다"); return; }
    const remaining = checkResult.remaining ?? 0;
    if (purchaseN > remaining) { setError(`구매 한도 초과 (잔여 구매 가능: ${formatAmount(remaining)})`); return; }
    setApplying(true); setError("");
    try {
      const r = await fetchApply({ phone, amount: discountAmt, purchaseAmount: purchaseN, store: selectedStore });
      if (r.ok) {
        setApplyResult({ ok: true, msg: "✅ 할인 적용 완료", discountAmt, purchaseAmt: purchaseN, remaining: r.remaining });
        setPurchase("");
        setCheckResult(prev => prev ? { ...prev, used: r.used, remaining: r.remaining } : prev);
      } else {
        const msgs: Record<string, string> = { LIMIT_EXCEEDED: "월 구매 한도를 초과했습니다", NOT_FOUND: "등록되지 않은 직원입니다", INACTIVE: "비활성화된 직원입니다" };
        setApplyResult({ ok: false, msg: msgs[r.reason] ?? "할인 적용 실패", discountAmt: 0, purchaseAmt: purchaseN });
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "적용 오류");
    } finally { setApplying(false); }
  }

  function reset() {
    setPhone(""); setCheckResult(null); setPurchase(""); setApplyResult(null); setError("");
    setPinInput(""); setPinVerified(false); setPinError("");
  }

  const displayPhone = phone.length === 11
    ? `${phone.slice(0,3)}-${phone.slice(3,7)}-${phone.slice(7)}`
    : phone.length === 10 ? `${phone.slice(0,3)}-${phone.slice(3,6)}-${phone.slice(6)}` : phone;

  const emp = checkResult?.employee;
  const remaining = checkResult?.remaining ?? 0;
  const limitN = checkResult?.limit ?? 300000;
  const usedN = checkResult?.used ?? 0;
  const pct = limitN > 0 ? Math.min(100, Math.round((usedN / limitN) * 100)) : 0;
  const purchaseN = parseInt(purchase.replace(/,/g, ""), 10) || 0;
  const effectiveRate = checkResult?.discountRate ?? discountRate;
  const discountAmt = Math.floor(purchaseN * effectiveRate / 100);
  const paymentAmt = purchaseN - discountAmt;
  const overLimit = purchaseN > remaining && remaining > 0;
  const needPin = !!discountPin;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b border-border bg-primary text-primary-foreground">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold tracking-tight">MIMILINE</span>
            <span className="text-xs opacity-70 font-medium">직원할인 시스템</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="#/usage" className="text-sm font-medium px-3 py-1.5 rounded-md bg-primary-foreground/15 hover:bg-primary-foreground/25 transition-colors">잔액 조회</a>
            <a href="#/admin" className="text-sm font-medium px-3 py-1.5 rounded-md bg-primary-foreground/15 hover:bg-primary-foreground/25 transition-colors">관리자 →</a>
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-lg mx-auto w-full px-4 py-5 flex flex-col gap-4">

        {/* 매장 선택 */}
        <div className="flex flex-col gap-1.5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">결제 매장</p>
          <div className="flex gap-2">
            {STORES.map(s => (
              <button key={s} onClick={() => setSelectedStore(s)}
                className={`flex-1 py-2 text-sm font-medium rounded-md border transition-colors ${
                  selectedStore === s ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/40"
                }`}>{s}</button>
            ))}
          </div>
        </div>

        {/* ① 전화번호 조회 */}
        <div className="bg-card rounded-lg border border-border p-4 flex flex-col gap-3">
          <p className="text-sm font-semibold">① 전화번호 조회</p>
          <div className="flex gap-2">
            <input type="tel" value={displayPhone} onChange={e => handlePhoneInput(e.target.value)}
              placeholder="010-0000-0000" onKeyDown={e => e.key === "Enter" && handleCheck()}
              className="flex-1 border border-border rounded-md px-3 py-2.5 text-base font-mono tracking-wider focus:outline-none focus:ring-2 focus:ring-ring bg-background" />
            <button onClick={handleCheck} disabled={checking || phone.length < 9}
              className="px-4 py-2.5 bg-primary text-primary-foreground text-sm font-medium rounded-md disabled:opacity-50 hover:opacity-90 transition-opacity">
              {checking ? "조회중…" : "조회"}
            </button>
          </div>
          {error && <p className="text-sm text-destructive font-medium">{error}</p>}
        </div>

        {/* 조회 결과 */}
        {checkResult && (
          <div className={`rounded-lg border p-4 flex flex-col gap-3 ${checkResult.ok ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}>
            {checkResult.reason === "NOT_FOUND" && (
              <div className="flex items-center gap-2"><span className="text-2xl">❌</span>
                <div><p className="font-semibold text-red-700">미등록 직원</p>
                <p className="text-sm text-red-600">{formatPhone(phone)} — 직원 명부에 없습니다</p></div>
              </div>
            )}
            {checkResult.reason === "INACTIVE" && (
              <div className="flex items-center gap-2"><span className="text-2xl">🚫</span>
                <div><p className="font-semibold text-red-700">비활성화 직원</p>
                <p className="text-sm text-red-600">{emp?.name} — 퇴사/비활성 처리된 직원입니다</p></div>
              </div>
            )}
            {checkResult.reason === "LIMIT_EXCEEDED" && emp && (
              <div className="flex items-center gap-2"><span className="text-2xl">⛔</span>
                <div><p className="font-semibold text-red-700">월 한도 초과</p>
                <p className="text-sm text-red-600">{emp.name} — 이번 달 한도를 모두 사용했습니다</p></div>
              </div>
            )}
            {checkResult.ok && emp && (
              <>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-lg flex-shrink-0">
                    {emp.name[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-foreground">{emp.name}</p>
                    <p className="text-sm text-muted-foreground">{[emp.department, emp.position, emp.store].filter(Boolean).join(" · ")}</p>
                  </div>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">할인 가능</span>
                </div>
                <div className="flex flex-col gap-1.5">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>이번달 구매: {formatAmount(usedN)}</span>
                    <span>잔여 구매 가능: <strong className="text-primary">{formatAmount(remaining)}</strong> / {formatAmount(limitN)}</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ② 결제 비밀번호 (PIN 설정된 경우에만) */}
        {checkResult?.ok && emp && needPin && !pinVerified && !applyResult?.ok && (
          <div className="bg-card rounded-lg border border-border p-4 flex flex-col gap-3">
            <p className="text-sm font-semibold">② 결제 비밀번호 확인</p>
            <p className="text-xs text-muted-foreground">이번달 결제 비밀번호를 입력해주세요.</p>
            <div className="flex gap-2">
              <input type="text" value={pinInput}
                onChange={e => { setPinInput(e.target.value); setPinError(""); }}
                onKeyDown={e => e.key === "Enter" && handleVerifyPin()}
                placeholder="비밀번호 입력"
                className="flex-1 border border-border rounded-md px-3 py-2.5 text-base font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-ring bg-background" />
              <button onClick={handleVerifyPin} disabled={!pinInput.trim()}
                className="px-4 py-2.5 bg-primary text-primary-foreground text-sm font-medium rounded-md disabled:opacity-50 hover:opacity-90 transition-opacity">
                확인
              </button>
            </div>
            {pinError && <p className="text-sm text-destructive font-medium">{pinError}</p>}
          </div>
        )}

        {/* ③ 할인 계산 */}
        {checkResult?.ok && emp && (pinVerified || !needPin) && !applyResult?.ok && (
          <div className="bg-card rounded-lg border border-border p-4 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold">{needPin ? "③" : "②"} 할인 계산</p>
              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-semibold">직원할인 {effectiveRate}%</span>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-muted-foreground">구매금액 (정가)</label>
              <div className="relative">
                <input type="number" value={purchase} onChange={e => { setPurchase(e.target.value); setError(""); }}
                  placeholder="예: 50000" min="1"
                  className="w-full border border-border rounded-md px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-ring bg-background pr-8" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">원</span>
              </div>
            </div>
            {purchaseN > 0 && (
              <div className={`rounded-lg p-3 flex flex-col gap-2 ${overLimit ? "bg-red-50 border border-red-200" : "bg-accent/40 border border-primary/20"}`}>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">정가</span><span className="font-medium">{formatAmount(purchaseN)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">할인금액 ({effectiveRate}%)</span>
                  <span className={`font-bold ${overLimit ? "text-destructive" : "text-primary"}`}>− {formatAmount(discountAmt)}</span>
                </div>
                <div className="flex justify-between border-t border-border pt-2 mt-1">
                  <span className="text-sm font-semibold">실제 결제금액</span>
                  <span className="text-base font-bold">{formatAmount(paymentAmt)}</span>
                </div>
                {overLimit && <p className="text-xs text-destructive font-medium">⚠ 이번달 구매 한도({formatAmount(remaining)}) 초과</p>}
              </div>
            )}
            {error && <p className="text-sm text-destructive font-medium">{error}</p>}
            <button onClick={handleApply} disabled={applying || !purchase || purchaseN <= 0 || discountAmt <= 0 || overLimit}
              className="w-full py-3 bg-primary text-primary-foreground text-sm font-semibold rounded-md disabled:opacity-40 hover:opacity-90 transition-opacity">
              {applying ? "처리중…" : purchaseN > 0 ? `${formatAmount(discountAmt)} 할인 적용` : "할인 적용"}
            </button>
          </div>
        )}

        {/* 적용 결과 */}
        {applyResult && (
          <div className={`rounded-lg border p-4 flex flex-col gap-3 ${applyResult.ok ? "border-green-300 bg-green-50" : "border-red-200 bg-red-50"}`}>
            <div className="flex items-center gap-3">
              <span className="text-2xl">{applyResult.ok ? "✅" : "❌"}</span>
              <p className={`font-bold text-base ${applyResult.ok ? "text-green-800" : "text-red-700"}`}>{applyResult.msg}</p>
            </div>
            {applyResult.ok && (
              <div className="flex flex-col gap-0 rounded-lg overflow-hidden border border-green-200">
                <div className="flex justify-between items-center px-4 py-2.5 bg-white">
                  <span className="text-sm text-gray-500">구매금액 (정가)</span>
                  <span className="text-sm font-medium text-gray-800">{formatAmount(applyResult.purchaseAmt)}</span>
                </div>
                <div className="flex justify-between items-center px-4 py-2.5 bg-white border-t border-green-100">
                  <span className="text-sm text-gray-500">할인금액 ({effectiveRate}%)</span>
                  <span className="text-sm font-bold text-primary">− {formatAmount(applyResult.discountAmt)}</span>
                </div>
                <div className="flex justify-between items-center px-4 py-3 bg-green-100 border-t border-green-200">
                  <span className="text-sm font-bold text-green-900">실제 결제금액</span>
                  <span className="text-lg font-extrabold text-green-900">{formatAmount(applyResult.purchaseAmt - applyResult.discountAmt)}</span>
                </div>
                {applyResult.remaining !== undefined && (
                  <div className="flex justify-between items-center px-4 py-2 bg-green-50 border-t border-green-200">
                    <span className="text-xs text-green-700">이번달 잔여 한도</span>
                    <span className="text-xs font-semibold text-green-700">{formatAmount(applyResult.remaining)}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {checkResult && (
          <button onClick={reset} className="w-full py-2.5 border border-border text-sm text-muted-foreground rounded-md hover:bg-muted/40 transition-colors">
            다음 직원 조회
          </button>
        )}
      </div>
    </div>
  );
}
